/*  ========================================================================
    # Components - Hero Slider Navigation
    ========================================================================  */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import useLocalization from '../hooks/useLocalization';
import useTranslation from '../hooks/useTranslation';

const HeroSliderNavigation = ({
  currentIndex,
  totalCount,
  onGoNext,
  onGoPrev,
  paused,
  onSetPaused,
  thumbnails,
  progress
}) => {
  const _ = useTranslation();

  // Icons
  const local = useLocalization();
  const iconChevronLeft = `${local('svgSprite')}#chevron-left`;
  const iconChevronRight = `${local('svgSprite')}#chevron-right`;
  const iconPause = `${local('svgSprite')}#pause`;
  const iconPlay = `${local('svgSprite')}#play`;

  // Calculate slide offset relative to animation direction
  const getOffset = useCallback((slideIndex, currentIndex, slidesCount) => {
    let numericOffset = slideIndex - currentIndex;

    if (numericOffset <= -1 * slidesCount + 2) {
      numericOffset += slidesCount;
    } else if (numericOffset >= slidesCount - 2) {
      numericOffset -= slidesCount;
    }

    // Clamp number between -2 and +2
    return Math.min(Math.max(numericOffset, -2), 2);
  }, []);

  return (
    <div className='c-hero-slider-navigation'>
      <button
        type='button'
        className='c-hero-slider-navigation__arrow c-hero-slider-navigation__arrow--left c-button c-button--no-hove c-button--icon'
        onClick={onGoPrev}
        aria-label={_('slider_prev')}
      >
        <svg viewBox='0 0 200 200'>
          <use href={iconChevronLeft}></use>
        </svg>
      </button>
      <div className='c-hero-slider-navigation__thumb-wrapper'>
        {thumbnails.map((thumbnail, i) => {
          const offset = getOffset(i, currentIndex, totalCount);
          const buttonHandler =
            (offset === 0 && (() => onSetPaused(!paused))) ||
            (offset === -1 && onGoPrev) ||
            (offset === 1 && onGoNext) ||
            undefined;
          const buttonTitle =
            (offset === 0 && (paused ? _('slider_resume') : _('slider_pause'))) ||
            (offset === -1 && _('slider_prev')) ||
            (offset === 1 && _('slider_next')) ||
            'Nicht verfügbar';

          return (
            <button
              key={i}
              data-offset={offset}
              data-paused={paused}
              style={{ '--dashoffset': 277 - progress * 277 }}
              className='c-hero-slider-navigation__thumb'
              onClick={buttonHandler}
              aria-label={buttonTitle}
            >
              <svg
                className='c-hero-slider-navigation__progress'
                viewBox='0 0 100 100'
                version='1.1'
              >
                <circle className='c-hero-slider-navigation__ring' cx='50' cy='50' r='44' />
              </svg>
              <img
                className='c-hero-slider-navigation__thumb-image'
                src={thumbnail.sizes?.thumbnail}
                alt={thumbnail.alt}
              />
              <svg className='c-hero-slider-navigation__pause' viewBox='0 0 200 200'>
                <use href={paused ? iconPlay : iconPause}></use>
              </svg>
            </button>
          );
        })}
      </div>
      <button
        type='button'
        className='c-hero-slider-navigation__arrow c-hero-slider-navigation__arrow--right c-button c-button--no-hove c-button--icon'
        onClick={onGoNext}
        aria-label={_('slider_next')}
      >
        <svg viewBox='0 0 200 200'>
          <use href={iconChevronRight}></use>
        </svg>
      </button>
    </div>
  );
};

HeroSliderNavigation.propTypes = {
  currentIndex: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onGoNext: PropTypes.func.isRequired,
  onGoPrev: PropTypes.func.isRequired,
  paused: PropTypes.bool,
  onSetPaused: PropTypes.func.isRequired,
  thumbnails: PropTypes.arrayOf(PropTypes.object).isRequired,
  progress: PropTypes.number.isRequired
};

export default HeroSliderNavigation;
